/**
 * EventListCards — a report's page of AI events as a GALLERY, the same shape the alert list
 * uses, for the same reason: an AI event is a claim about something a camera SAW, and the
 * frame settles it faster than any row of columns can describe it.
 *
 * Paired with the report table, not a replacement for it. The table is how you compare
 * twenty-five events on one screen — the confidences down a column, the plates aligned. The
 * cards are how you find the one you're after. The report page keeps both and remembers which.
 *
 * WHAT A CARD SAYS, and where it says it:
 *   • over the frame — WHEN (top right), then WHAT the model called this event and WHERE, in
 *     the scrim along the bottom. Those three are on every event of every model, so they are
 *     the part of the card that never moves.
 *   • under it — the model's own headline facts, taken from the SAME per-model columns the
 *     table and the record use (`splitEventFacts`), so a plate or a violation type can't read
 *     one way here and another way there. Timestamp and camera are dropped from that list:
 *     they're already over the picture, and a card that prints them twice wastes the two
 *     lines a face's identity or a fire's type should have had.
 *
 * The frame is a button and so is "Details" — the picture is the obvious way in, and the
 * named control is there for the reader who doesn't know that.
 */
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import {
  ArrowRightIcon,
  MagnifyingGlassIcon,
  PhotoIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';
import { useAlertImage } from '../alerts/useAlertImage';
import { whenStamp } from '../alerts/alertVocab';
import {
  eventTypeLabel,
  splitEventFacts,
  type EventColumnLike,
  type EventFact,
  type EventValueContext,
} from './EventDetail';

/** Already over the picture — printing them again under it would cost two of four fact slots. */
const OVERLAID = new Set(['event_timestamp', 'camera_id']);

export interface EventListCardsProps {
  events: any[];
  /** The model's column definitions — the card's facts come from these. */
  columns: EventColumnLike[];
  /**
   * Per-card columns, for a grid that mixes kinds — a person card shows clothing, a vehicle card
   * its type and colour, and neither prints the other's fields as a row of "—". Wins over `columns`.
   */
  columnsFor?: (event: any) => EventColumnLike[];
  loading?: boolean;
  /** Camera and person name resolvers, as the record uses them. */
  context?: EventValueContext;
  /** The event's frame, as a URL the browser can load directly (null → placeholder). */
  imageFor?: (event: any) => string | null;
  /** Lazy resolver for hosts that must stream frames (the cloud, through its node tunnel). */
  loadImage?: (event: any) => Promise<string | null>;
  /** The big line over the frame. Defaults to what the model called the event. */
  titleFor?: (event: any) => string;
  /**
   * A small label in the frame's top-left corner — which model saw it, on a grid that mixes
   * models. Absent (or null) draws no badge, which is every report grid: one model per page.
   */
  badgeFor?: (event: any) => string | null | undefined;
  /** How many facts to print under the frame. Four fills two tidy rows. */
  factLimit?: number;
  onOpen: (index: number) => void;
  /** One extra control on a card's foot — e.g. "Register" on an unknown face. */
  cardAction?: (event: any) => ReactNode;
  /** Replaces the empty state's sentence (a dead deep link says something different). */
  emptyTitle?: string;
  emptyHint?: ReactNode;
  /**
   * Size the cards to the height this grid is actually given, rather than to a fixed column
   * width — WIDTH AND HEIGHT BOTH, by dropping to a denser tier before it narrows the frame.
   * Opt-in: only the surfaces that live in a resizable column want it, and a caller that does
   * not pass it gets exactly the grid it always had. See `fitCardLayout`.
   */
  fitToHeight?: boolean;
}

/** Fills whatever width it is given rather than a fixed column count — see AlertListCards. */
const GRID = 'grid gap-4 p-4 [grid-template-columns:repeat(auto-fill,minmax(19rem,1fr))]';

/**
 * ── SIZING A CARD TO THE ROOM IT HAS ──
 *
 * A card is a 16:9 frame, a grid of facts and a foot. Its height is therefore its width times
 * 9/16 plus furniture, which is why shrinking a card is not one number: narrowing it alone buys
 * only the frame back, and on a short container the furniture is most of the card. The result
 * was cards that stayed ~330px tall in a 400px box — one row, half of it wasted on padding.
 *
 * So the fit picks a DENSITY as well as a width. A denser tier prints fewer facts, tightens the
 * padding and the foot, and lets the frame come down with the width. Each tier has its own floor
 * on width, because the point at which a plate or a timestamp truncates depends on the type size
 * around it — and below that floor a scrollbar is the honest answer, not a smaller card.
 */
export type CardDensity = 'comfortable' | 'compact' | 'dense';

interface DensityTier {
  density: CardDensity;
  /** Foot + the facts grid's own vertical padding. */
  furniture: number;
  /** One ROW of facts (the grid prints two per row). */
  factRow: number;
  /** The most facts this tier will print, however many the model has. */
  maxFacts: number;
  minWidth: number;
  maxWidth: number;
}

const TIERS: DensityTier[] = [
  { density: 'comfortable', furniture: 61, factRow: 40, maxFacts: 99, minWidth: 13 * 16, maxWidth: 24 * 16 },
  { density: 'compact', furniture: 49, factRow: 32, maxFacts: 4, minWidth: 11 * 16, maxWidth: 19 * 16 },
  { density: 'dense', furniture: 42, factRow: 26, maxFacts: 2, minWidth: 9 * 16, maxWidth: 15 * 16 },
];

const CARD_W_DEFAULT = 19 * 16;

/** Below this the frame stops being a picture of anything. */
const MEDIA_MIN = 64;

export interface CardLayout {
  width: number;
  density: CardDensity;
  /** How many fact tiles this tier will print. */
  facts: number;
}

/** The frame at the comfortable width — a card is never grown past this, only shrunk. */
const MEDIA_IDEAL = Math.round((CARD_W_DEFAULT * 9) / 16);

/**
 * The card layout for the height this grid has been given.
 *
 * TWO RULES, and the order of them is the whole design:
 *
 *   1. AIM FOR TWO ROWS (three when the room is genuinely tall). One row of results is a
 *      letterbox — the operator scrolls to learn whether there is a second match, which is the
 *      work the page exists to save.
 *   2. NEVER GROW A CARD TO FILL THE ROW, only shrink it to fit. Filling was the bug: in a
 *      400px box the card expanded to 368px, so the one thing a short container produced was a
 *      bigger card. Slack under the last row is fine; a card that has eaten the container is not.
 *
 * Shrinking goes through the tiers in order, because the cheapest height to give up is the one
 * the operator reads least: first the fact tiles it prints, then its padding and type size, and
 * only then the frame. Each tier stops at its own width floor — past that the plate truncates,
 * and a scrollbar is a better answer than an unreadable card.
 */
export function fitCardLayout(availableHeight: number, factLimit: number): CardLayout {
  const GAP = 16;
  const comfortable: CardLayout = {
    width: CARD_W_DEFAULT, density: 'comfortable', facts: factLimit,
  };
  if (!Number.isFinite(availableHeight) || availableHeight <= 0) return comfortable;

  const target = availableHeight >= 1000 ? 3 : 2;

  for (let rows = target; rows >= 1; rows--) {
    const rowHeight = (availableHeight - GAP * (rows + 1)) / rows;

    for (const tier of TIERS) {
      const facts = Math.min(factLimit, tier.maxFacts);
      const furniture = tier.furniture + Math.ceil(facts / 2) * tier.factRow;
      // `min` is rule 2: the row may be taller than the card wants to be, and that is slack,
      // not licence.
      const media = Math.min(rowHeight - furniture, MEDIA_IDEAL);
      if (media < MEDIA_MIN) continue;

      // The frame is 16:9, so the width that makes the card exactly this tall IS its width.
      const width = (media * 16) / 9;
      if (width < tier.minWidth) continue;
      return { width: Math.round(Math.min(tier.maxWidth, width)), density: tier.density, facts };
    }
  }

  // Not even one row of the densest card fits: draw it anyway, and let the grid scroll.
  const last = TIERS[TIERS.length - 1];
  return { width: last.minWidth, density: last.density, facts: Math.min(factLimit, last.maxFacts) };
}

function EventCardMedia({
  event,
  title,
  badge,
  camera,
  imageFor,
  loadImage,
}: {
  event: any;
  title: string;
  badge?: string | null;
  camera: string;
  imageFor?: (event: any) => string | null;
  loadImage?: (event: any) => Promise<string | null>;
}) {
  const { src, markFailed, ref } = useAlertImage(event, imageFor, loadImage);

  return (
    <div ref={ref} className="relative aspect-video w-full overflow-hidden bg-neutral-900">
      {/* Painted whether or not a frame lands, so the white type over it never flips contrast
          when one does. */}
      <div className="absolute inset-0 bg-gradient-to-br from-neutral-800 to-neutral-950" />
      {src ? (
        <img
          src={src}
          alt=""
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover"
          onError={markFailed}
        />
      ) : (
        <PhotoIcon className="absolute left-1/2 top-1/2 h-7 w-7 -translate-x-1/2 -translate-y-1/2 text-white/20" />
      )}
      <div className="absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-black/85 via-black/45 to-transparent" />

      <div className="absolute inset-x-0 top-0 flex items-start justify-end gap-2 p-2.5">
        {badge && (
          <span className="mr-auto min-w-0 truncate rounded-md bg-white px-2 py-1 text-[11px] font-semibold text-neutral-900 shadow-sm" title={badge}>
            {badge}
          </span>
        )}
        <span className="shrink-0 rounded-md bg-black/60 px-2 py-1 text-[11px] font-medium tabular-nums text-white">
          {whenStamp(event?.event_timestamp)}
        </span>
      </div>

      <div className="absolute inset-x-0 bottom-0 p-3">
        <p className="truncate text-[15px] font-semibold leading-tight text-white" title={title}>
          {title}
        </p>
        <p className="mt-1 flex items-center gap-1.5 text-[12px] text-white/80">
          <VideoCameraIcon className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate" title={camera}>{camera || '—'}</span>
        </p>
      </div>
    </div>
  );
}

export default function EventListCards({
  events,
  columns,
  columnsFor,
  loading,
  context = {},
  imageFor,
  loadImage,
  titleFor,
  badgeFor,
  factLimit = 4,
  onOpen,
  cardAction,
  emptyTitle = 'No events found for the selected filters.',
  emptyHint,
  fitToHeight = false,
}: EventListCardsProps) {
  /**
   * The grid measures the box it was put in, not the window: the answer's height here is what
   * the filter panel and the dock below left of it, and both of those move under the operator.
   */
  const gridRef = useRef<HTMLDivElement>(null);
  const [boxHeight, setBoxHeight] = useState(0);

  useLayoutEffect(() => {
    if (!fitToHeight) return undefined;
    const el = gridRef.current?.parentElement;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([entry]) => {
      const h = entry.contentRect.height;
      // Sub-pixel churn is not a change worth a render.
      setBoxHeight((prev) => (Math.abs(h - prev) < 4 ? prev : h));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [fitToHeight]);

  /**
   * The layout this grid is drawing at — width AND density, both from the height it was given.
   * `comfortable` with the caller's own fact limit is the un-measured default, so a grid that
   * never opts in behaves exactly as it always did.
   */
  const layout: CardLayout = fitToHeight && boxHeight > 0
    ? fitCardLayout(boxHeight, factLimit)
    : { width: CARD_W_DEFAULT, density: 'comfortable', facts: factLimit };
  const gridStyle = fitToHeight && boxHeight > 0
    ? { gridTemplateColumns: `repeat(auto-fill, minmax(${layout.width}px, 1fr))` }
    : undefined;

  /** What each tier changes about a card, beyond how many facts it prints. */
  const skin = layout.density === 'dense'
    ? { facts: 'gap-x-2 gap-y-1 px-2.5 py-1.5', dt: 'text-[9px]', dd: 'text-[11px]', foot: 'px-2 py-1', details: 'text-[11px]' }
    : layout.density === 'compact'
      ? { facts: 'gap-x-2.5 gap-y-1.5 px-3 py-2', dt: 'text-[9.5px]', dd: 'text-[12px]', foot: 'px-2.5 py-1.5', details: 'text-[12px]' }
      : { facts: 'gap-x-3 gap-y-2 px-4 py-3', dt: 'text-[10px]', dd: 'text-[13px]', foot: 'px-3 py-2', details: 'text-[13px]' };
  if (loading) {
    return (
      <div ref={gridRef} className={GRID} style={gridStyle}>
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div
            key={i}
            className="animate-pulse rounded-xl bg-muted"
            /* The height the real cards will land at, so the grid does not jump when they
               arrive — and so the skeleton itself honours the room available. */
            style={{ height: Math.round((layout.width * 9) / 16) + 61 + Math.ceil(layout.facts / 2) * 40 }}
          />
        ))}
      </div>
    );
  }

  if (!events.length) {
    return (
      <div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
        <MagnifyingGlassIcon className="h-8 w-8 text-muted-foreground opacity-40" />
        <p className="text-sm font-medium text-foreground">{emptyTitle}</p>
        {emptyHint && <p className="text-[13px] text-muted-foreground">{emptyHint}</p>}
      </div>
    );
  }

  return (
    <div ref={gridRef} className={GRID} style={gridStyle}>
      {events.map((event, idx) => {
        const { facts, fields } = splitEventFacts(event, columnsFor ? columnsFor(event) : columns, context);
        // Headline facts first, then the supporting ones to fill the tiles the model didn't
        // earn — a person_count event has no identity or direction, and four empty slots
        // would make it look like a broken card rather than a quiet one.
        const shown: EventFact[] = [...facts, ...fields]
          .filter((f) => !OVERLAID.has(f.key))
          .slice(0, layout.facts);
        const camera = context.cameraName?.(event?.camera_id) || String(event?.camera_id || '').slice(0, 8);
        const title = titleFor?.(event) || eventTypeLabel(event) || 'Event';
        const action = cardAction?.(event);

        return (
          <article
            /* A search result is one SUBJECT, and a frame of six people is six cards sharing
               one event_id — so the event id alone is not a key. The index is the fallback it
               always was, for the lists here that carry neither. */
            key={event.id || (event.event_id ? `${event.event_id}:${event.subject_seq ?? 0}` : idx)}
            className="flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-card transition-shadow hover:shadow-card-hover"
          >
            <button
              type="button"
              onClick={() => onOpen(idx)}
              aria-label={`Open ${title}`}
              className="block w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand"
            >
              <EventCardMedia
                event={event}
                title={title}
                badge={badgeFor?.(event)}
                camera={camera}
                imageFor={imageFor}
                loadImage={loadImage}
              />
            </button>

            {shown.length > 0 && (
              <dl className={`grid grid-cols-2 ${skin.facts}`}>
                {shown.map((f) => (
                  <div key={f.key} className="min-w-0">
                    <dt className={`truncate font-semibold uppercase tracking-wider text-muted-foreground ${skin.dt}`}>
                      {f.label}
                    </dt>
                    <dd className={`truncate text-foreground ${skin.dd} ${f.mono ? 'font-mono' : ''}`}>
                      {f.value}
                    </dd>
                  </div>
                ))}
              </dl>
            )}

            {/* `mt-auto` pins the foot down, so cards in a row line their controls up however
                many facts the model above them had. */}
            <div className={`mt-auto flex items-center justify-between gap-2 border-t border-border bg-muted ${skin.foot}`}>
              <div className="flex min-w-0 items-center gap-1">{action}</div>
              <button
                type="button"
                onClick={() => onOpen(idx)}
                className={`flex shrink-0 items-center gap-1 rounded-md px-2 py-1 font-semibold text-brand transition-colors hover:bg-muted ${skin.details}`}
              >
                Details
                <ArrowRightIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          </article>
        );
      })}
    </div>
  );
}
